import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, LogOut, RefreshCw, Mail, Phone, Building2, Sparkles, ShieldCheck } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { API } from "@/lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "saavi-admin-key";

function AdminLogin({ onAuthed }) {
  const [key, setKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/verify`, { headers: { "X-Admin-Key": key.trim() } });
      if (!res.ok) throw new Error("Invalid key");
      window.localStorage.setItem(STORAGE_KEY, key.trim());
      toast.success("Welcome back, admin.");
      onAuthed(key.trim());
    } catch (err) {
      toast.error("Invalid admin key");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-pad">
      <div className="container-saavi max-w-md">
        <div className="border border-border rounded-md p-8 bg-card">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-saavi-blue">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="mt-6 text-3xl">Admin Access</h1>
          <p className="mt-2 text-sm text-muted-foreground">Enter your admin API key to view lead submissions.</p>
          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-key" className="text-sm">API Key</Label>
              <Input
                id="admin-key"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="X-Admin-Key"
                className="mt-1.5 h-11"
                data-testid="admin-key-input"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={submitting || !key.trim()} className="w-full h-11 gap-2" data-testid="admin-login-submit">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function LeadsList({ apiKey, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/contact?limit=500`, { headers: { "X-Admin-Key": apiKey } });
      if (res.status === 401) {
        toast.error("Session expired");
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiKey, onLogout]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="section-pad">
        <div className="container-saavi flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading leads…
        </div>
      </section>
    );
  }

  return (
    <section className="section-pad">
      <div className="container-saavi">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="font-mono-overline text-saavi-blue mb-2">Admin · Leads</div>
            <h1 className="text-4xl md:text-5xl">Contact submissions</h1>
            <p className="mt-2 text-muted-foreground">Latest first · {leads.length} total</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={refreshing} data-testid="admin-refresh" className="h-10 gap-2">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
            <Button variant="outline" onClick={onLogout} data-testid="admin-logout" className="h-10 gap-2">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="border border-border rounded-md p-12 text-center bg-card">
            <Sparkles className="h-8 w-8 text-saavi-blue mx-auto" />
            <h3 className="mt-4 text-xl">No leads yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">New submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-leads-grid">
            {leads.map((l) => (
              <article key={l.id} data-testid={`admin-lead-${l.id}`} className="card-hover border border-border rounded-md p-6 bg-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-extrabold tracking-tight">{l.name}</h3>
                    <div className="mt-1 text-xs font-mono-overline text-muted-foreground">{fmtDate(l.created_at)}</div>
                  </div>
                  {l.service && <Badge variant="secondary" className="font-mono-overline">{l.service}</Badge>}
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-saavi-blue">
                    <Mail className="h-4 w-4 text-saavi-blue" /> {l.email}
                  </a>
                  {l.phone && (
                    <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-saavi-green">
                      <Phone className="h-4 w-4 text-saavi-green" /> {l.phone}
                    </a>
                  )}
                  {l.company && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="h-4 w-4 text-saavi-red" /> {l.company}
                    </div>
                  )}
                </div>
                <p className="mt-4 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">{l.message}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Admin() {
  useSEO("/admin");
  const [apiKey, setApiKey] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [checking, setChecking] = useState(!!apiKey);

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!apiKey) { setChecking(false); return; }
      try {
        const res = await fetch(`${API}/admin/verify`, { headers: { "X-Admin-Key": apiKey } });
        if (!cancelled) {
          if (!res.ok) {
            window.localStorage.removeItem(STORAGE_KEY);
            setApiKey(null);
          }
          setChecking(false);
        }
      } catch {
        if (!cancelled) setChecking(false);
      }
    }
    if (apiKey) verify();
    return () => { cancelled = true; };
  }, [apiKey]);

  const logout = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setApiKey(null);
    toast.success("Logged out");
  };

  if (checking) {
    return (
      <section className="section-pad container-saavi flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Verifying session…
      </section>
    );
  }
  if (!apiKey) return <AdminLogin onAuthed={setApiKey} />;
  return <LeadsList apiKey={apiKey} onLogout={logout} />;
}
